/**
 * DealGroupedView — Visualização "Lista Agrupada Por:" da listagem de Negócios
 *
 * Espelho de `TaskGroupedView` (módulo de Tarefas): renderiza a mesma fonte de
 * dados (`useCrmDealsPaginated`) em seções colapsáveis agrupadas por um campo
 * escolhido pelo usuário — Status, Vendedor, Workspace, Origem, Cadastro,
 * Probabilidade (faixas de 25%) e Pipeline (aninhando Pipeline → Fase).
 *
 * Princípios de reuso:
 * - Cada grupo expandido monta `<DealsListTable />` — exatamente a mesma tabela
 *   da listagem padrão, com todas as ações e permissões vindas do
 *   `DealsListTableContext` provido pela página.
 * - A contagem do cabeçalho usa a própria RPC com `pageSize: 1` (leitura leve).
 * - Dentro do grupo há paginação por setas, 10 registros por página.
 * - As fases só aparecem sob o pipeline ao qual pertencem (mesma regra que o
 *   módulo de Tarefas aplica em Categoria → Fase).
 *
 * @version 1.1.0
 * RELEASE: 2026-09-07 v1.1.0 - Agrupamento de funil passa a ter três níveis
 *          (Workspace → Pipeline → Fase), evitando funis homônimos de
 *          workspaces diferentes aparecerem indistinguíveis.
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial

 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, User, Building2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useCrmDealsPaginated } from '@/hooks/useCrmDealsPaginated';
import type { DealSortField, SortDirection } from '@/hooks/useCrmDealsPaginated';
import type { DealFilters } from '@/hooks/useCrmDeals';
import { useCrmSellers } from '@/hooks/useCrmSellers';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useLeadSources } from '@/hooks/useLeadSources';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { DealsListTable } from '@/components/crm/deals/list/DealsListTable';
import { useDealGroupExpandedState } from '@/hooks/useDealsListViewPreference';

/** Campos disponíveis para agrupamento da listagem de Negócios. */
export type DealGroupBy =
  | 'status'
  | 'seller_id'
  | 'workspace_id'
  | 'source'
  | 'creation_origin'
  | 'probability'
  | 'pipeline_id';

/** Definição de um grupo (cabeçalho + filtro extra aplicado à RPC). */
interface GroupDef {
  key: string;
  label: string;
  colorHex?: string;
  colorClass?: string;
  leading?: ReactNode;
  /** Filtros adicionais mesclados aos filtros da tela. */
  filterPatch: Partial<DealFilters>;
}

/** Registros por página dentro de cada grupo. */
const GROUP_PAGE_SIZE = 10;

/** Rótulos de status (mesma nomenclatura da listagem/kanban). */
const STATUS_GROUPS: GroupDef[] = [
  { key: 'open', label: 'Em aberto', colorClass: 'bg-blue-500', filterPatch: { status: 'open' } },
  { key: 'won', label: 'Ganhos', colorClass: 'bg-emerald-500', filterPatch: { status: 'won' } },
  { key: 'lost', label: 'Perdidos', colorClass: 'bg-rose-500', filterPatch: { status: 'lost' } },
];

/** Origem do cadastro (mesmos tipos aceitos pelo badge "Cadastro"). */
const CREATION_ORIGIN_GROUPS: GroupDef[] = [
  { key: 'manual', label: 'Manual', filterPatch: { creation_origin_types: ['manual'] } },
  { key: 'import', label: 'Importação', filterPatch: { creation_origin_types: ['import'] } },
  { key: 'webhook', label: 'Webhook', filterPatch: { creation_origin_types: ['webhook'] } },
  { key: 'scheduling', label: 'Agendamento', filterPatch: { creation_origin_types: ['scheduling'] } },
  { key: 'flow', label: 'Fluxo', filterPatch: { creation_origin_types: ['flow'] } },
  { key: 'api', label: 'API', filterPatch: { creation_origin_types: ['api'] } },
];

/** Faixas de probabilidade de 25%. */
const PROBABILITY_GROUPS: GroupDef[] = [
  { key: '0-25', label: '0% a 25%', colorClass: 'bg-red-500', filterPatch: { probability_min: 0, probability_max: 25 } },
  { key: '26-50', label: '26% a 50%', colorClass: 'bg-amber-500', filterPatch: { probability_min: 26, probability_max: 50 } },
  { key: '51-75', label: '51% a 75%', colorClass: 'bg-sky-500', filterPatch: { probability_min: 51, probability_max: 75 } },
  { key: '76-100', label: '76% a 100%', colorClass: 'bg-emerald-500', filterPatch: { probability_min: 76, probability_max: 100 } },
];

interface DealGroupedViewProps {
  /** Filtros já montados pela tela (mesmo objeto usado na listagem/exportação). */
  baseFilters: DealFilters;
  groupBy: DealGroupBy;
  sortField: DealSortField;
  sortDirection: SortDirection;
  defaultExpanded?: boolean;
  expandAllSignal?: number;
  expandAllValue?: boolean;
}

export function DealGroupedView({
  baseFilters,
  groupBy,
  sortField,
  sortDirection,
  defaultExpanded = false,
  expandAllSignal = 0,
  expandAllValue = false,
}: DealGroupedViewProps) {
  const { sellers } = useCrmSellers();
  const { workspaces } = useCrmWorkspacesHierarchy();
  const { activeSources } = useLeadSources();

  /**
   * v1.1.0: o agrupamento "Workspace, Pipeline e Fase" aninha três níveis
   * (Workspace → Pipeline → Fase), porque funis de workspaces diferentes podem
   * ter o mesmo nome e ficavam indistinguíveis. As fases continuam vindo apenas
   * do funil correspondente. Filtros de workspace/pipeline já aplicados na tela
   * restringem a árvore.
   */
  const workspaceTree = useMemo(() => {
    return (workspaces || [])
      .filter((w) => !baseFilters.workspace_id || w.id === baseFilters.workspace_id)
      .map((w) => ({
        id: w.id,
        name: w.name,
        pipelines: (w.pipelines || [])
          .filter((p) => !baseFilters.pipeline_id || p.id === baseFilters.pipeline_id)
          .map((p) => ({ id: p.id, name: p.name })),
      }))
      .filter((w) => w.pipelines.length > 0);
  }, [workspaces, baseFilters.workspace_id, baseFilters.pipeline_id]);


  const groups: GroupDef[] = useMemo(() => {
    switch (groupBy) {
      case 'status':
        return STATUS_GROUPS;

      case 'creation_origin':
        return CREATION_ORIGIN_GROUPS;

      case 'probability':
        return PROBABILITY_GROUPS;

      case 'seller_id': {
        const list: GroupDef[] = (sellers || []).map((s) => ({
          key: s.id,
          label: s.user?.full_name || s.name,
          leading: (
            <SmartAvatar
              src={s.user?.avatar_url || s.avatar_url}
              name={s.user?.full_name || s.name}
              size="xs"
              use3DFallback
            />
          ),
          filterPatch: { seller_id: s.id },
        }));
        list.push({
          key: 'none',
          label: 'Sem vendedor',
          leading: <User className="h-4 w-4 text-muted-foreground" />,
          filterPatch: { seller_is_null: true },
        });
        return list;
      }

      case 'workspace_id': {
        const list: GroupDef[] = (workspaces || []).map((w) => ({
          key: w.id,
          label: w.name,
          colorHex: w.color || undefined,
          filterPatch: { workspace_id: w.id },
        }));
        list.push({
          key: 'none',
          label: 'Sem workspace',
          leading: <Building2 className="h-4 w-4 text-muted-foreground" />,
          filterPatch: { workspace_is_null: true },
        });
        return list;
      }

      case 'source': {
        const list: GroupDef[] = (activeSources || []).map((s) => ({
          key: s.id,
          label: s.label ?? s.id,
          filterPatch: { source_ids: [s.id] },
        }));
        list.push({
          key: 'none',
          label: 'Sem origem',
          filterPatch: { source_is_null: true },
        });
        return list;
      }

      default:
        return [];
    }
  }, [groupBy, sellers, workspaces, activeSources]);

  // Workspace → Pipeline → Fase (v1.1.0)
  if (groupBy === 'pipeline_id') {
    if (workspaceTree.length === 0) {
      return (
        <div className="flex items-center justify-center h-32 text-xs text-muted-foreground">
          Carregando opções de agrupamento...
        </div>
      );
    }
    return (
      <div className="flex flex-col">
        {workspaceTree.map((w) => (
          <DealWorkspaceGroupSection
            key={`workspace:${w.id}`}
            workspace={w}
            baseFilters={baseFilters}
            sortField={sortField}
            sortDirection={sortDirection}
            defaultExpanded={defaultExpanded}
            expandAllSignal={expandAllSignal}
            expandAllValue={expandAllValue}
          />
        ))}
      </div>
    );
  }


  if (groups.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-xs text-muted-foreground">
        Carregando opções de agrupamento...
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {groups.map((group) => (
        <DealGroupSection
          key={`${groupBy}:${group.key}`}
          group={group}
          scope={`${groupBy}:${group.key}`}
          baseFilters={baseFilters}
          sortField={sortField}
          sortDirection={sortDirection}
          defaultExpanded={defaultExpanded}
          expandAllSignal={expandAllSignal}
          expandAllValue={expandAllValue}
        />
      ))}
    </div>
  );
}

/* =========================================================================
 * Seção de WORKSPACE (v1.1.0) — envelopa os pipelines daquele workspace
 * ========================================================================= */
interface DealWorkspaceGroupSectionProps {
  workspace: { id: string; name: string; pipelines: { id: string; name: string }[] };
  baseFilters: DealFilters;
  sortField: DealSortField;
  sortDirection: SortDirection;
  defaultExpanded?: boolean;
  expandAllSignal?: number;
  expandAllValue?: boolean;
}

function DealWorkspaceGroupSection({
  workspace,
  baseFilters,
  sortField,
  sortDirection,
  defaultExpanded = false,
  expandAllSignal = 0,
  expandAllValue = false,
}: DealWorkspaceGroupSectionProps) {
  const [expanded, setExpanded] = useDealGroupExpandedState(
    `workspace:${workspace.id}`,
    defaultExpanded
  );

  useEffect(() => {
    if (expandAllSignal > 0) setExpanded(expandAllValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAllSignal]);

  // Contagem do workspace inteiro (leitura leve com pageSize 1)
  const workspaceFilters: DealFilters = useMemo(
    () => ({ ...baseFilters, workspace_id: workspace.id }),
    [baseFilters, workspace.id]
  );

  const { totalCount, isLoading: countLoading } = useCrmDealsPaginated(workspaceFilters, {
    page: 1,
    pageSize: 1,
    sortField,
    sortDirection,
  });

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 px-4 py-3 text-left',
          'bg-muted/30 hover:bg-muted/50 transition-colors',
          expanded && 'bg-muted/50'
        )}
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            expanded && 'rotate-90'
          )}
        />
        <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-sm font-semibold truncate">{workspace.name}</span>
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
          {countLoading ? '…' : totalCount}
        </Badge>
      </button>

      {expanded && (
        <div className="pl-4 border-l border-border/40 ml-4">
          {workspace.pipelines.map((p) => (
            <DealPipelineGroupSection
              key={`pipeline:${p.id}`}
              pipeline={p}
              baseFilters={workspaceFilters}
              sortField={sortField}
              sortDirection={sortDirection}
              defaultExpanded={defaultExpanded}
              expandAllSignal={expandAllSignal}
              expandAllValue={expandAllValue}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * Seção de PIPELINE — envelopa as fases daquele pipeline (e somente delas)
 * ========================================================================= */
interface DealPipelineGroupSectionProps {
  pipeline: { id: string; name: string };

  baseFilters: DealFilters;
  sortField: DealSortField;
  sortDirection: SortDirection;
  defaultExpanded?: boolean;
  expandAllSignal?: number;
  expandAllValue?: boolean;
}

function DealPipelineGroupSection({
  pipeline,
  baseFilters,
  sortField,
  sortDirection,
  defaultExpanded = false,
  expandAllSignal = 0,
  expandAllValue = false,
}: DealPipelineGroupSectionProps) {
  const [expanded, setExpanded] = useDealGroupExpandedState(
    `pipeline:${pipeline.id}`,
    defaultExpanded
  );

  useEffect(() => {
    if (expandAllSignal > 0) setExpanded(expandAllValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAllSignal]);

  // Fases apenas deste pipeline (evita fase de outro funil no grupo)
  const { phases } = usePipelinePhaseStats(expanded ? pipeline.id : null);

  const pipelineFilters: DealFilters = useMemo(
    () => ({ ...baseFilters, pipeline_id: pipeline.id, phase_id: null }),
    [baseFilters, pipeline.id]
  );

  const { totalCount, isLoading: countLoading } = useCrmDealsPaginated(pipelineFilters, {
    page: 1,
    pageSize: 1,
    sortField,
    sortDirection,
  });

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 px-4 py-3 text-left',
          'bg-muted/20 hover:bg-muted/40 transition-colors',
          expanded && 'bg-muted/40'
        )}
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            expanded && 'rotate-90'
          )}
        />
        <span className="text-sm font-semibold truncate">{pipeline.name}</span>
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
          {countLoading ? '…' : totalCount}
        </Badge>
        {/* v1.1.0: o nome do workspace passou para o nível pai (Workspace) */}

      </button>

      {expanded && (
        <div className="pl-6 border-l border-border/40 ml-4">
          {phases.length === 0 ? (
            <div className="px-6 py-3 text-xs text-muted-foreground italic">
              Nenhuma fase neste pipeline.
            </div>
          ) : (
            phases.map((ph) => (
              <DealGroupSection
                key={`phase:${ph.phase_id}`}
                group={{
                  key: ph.phase_id,
                  label: ph.phase_name,
                  colorHex: ph.phase_color || undefined,
                  filterPatch: { pipeline_id: pipeline.id, phase_id: ph.phase_id },
                }}
                scope={`phase:${ph.phase_id}`}
                baseFilters={baseFilters}
                sortField={sortField}
                sortDirection={sortDirection}
                defaultExpanded={defaultExpanded}
                expandAllSignal={expandAllSignal}
                expandAllValue={expandAllValue}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * Seção colapsável de GRUPO (cabeçalho + conteúdo lazy)
 * ========================================================================= */
interface DealGroupSectionProps {
  group: GroupDef;
  scope: string;
  baseFilters: DealFilters;
  sortField: DealSortField;
  sortDirection: SortDirection;
  defaultExpanded?: boolean;
  expandAllSignal?: number;
  expandAllValue?: boolean;
}

function DealGroupSection({
  group,
  scope,
  baseFilters,
  sortField,
  sortDirection,
  defaultExpanded = false,
  expandAllSignal = 0,
  expandAllValue = false,
}: DealGroupSectionProps) {
  const [expanded, setExpanded] = useDealGroupExpandedState(scope, defaultExpanded);

  useEffect(() => {
    if (expandAllSignal > 0) setExpanded(expandAllValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAllSignal]);

  const mergedFilters: DealFilters = useMemo(
    () => ({ ...baseFilters, ...group.filterPatch }),
    [baseFilters, group.filterPatch]
  );

  // Contagem leve do cabeçalho (pageSize=1 — lê apenas o total)
  const { totalCount, isLoading: countLoading } = useCrmDealsPaginated(mergedFilters, {
    page: 1,
    pageSize: 1,
    sortField,
    sortDirection,
  });

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 px-4 py-2.5 text-left',
          'hover:bg-muted/40 transition-colors',
          expanded && 'bg-muted/30'
        )}
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            expanded && 'rotate-90'
          )}
        />
        {group.leading ? (
          group.leading
        ) : group.colorHex ? (
          <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: group.colorHex }}
          />
        ) : group.colorClass ? (
          <span className={cn('w-3 h-3 rounded-full shrink-0', group.colorClass)} />
        ) : null}
        <span className="text-sm font-medium truncate">{group.label}</span>
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
          {countLoading ? '…' : totalCount}
        </Badge>
      </button>

      {expanded && (
        <DealGroupSectionContent
          mergedFilters={mergedFilters}
          sortField={sortField}
          sortDirection={sortDirection}
        />
      )}
    </div>
  );
}

/* =========================================================================
 * Conteúdo lazy de uma seção — reusa <DealsListTable /> com paginação
 * própria (setas), 10 registros por página.
 * ========================================================================= */
interface DealGroupSectionContentProps {
  mergedFilters: DealFilters;
  sortField: DealSortField;
  sortDirection: SortDirection;
}

function DealGroupSectionContent({
  mergedFilters,
  sortField,
  sortDirection,
}: DealGroupSectionContentProps) {
  const [page, setPage] = useState(1);

  // Volta para a primeira página quando filtros/ordenação mudam
  useEffect(() => {
    setPage(1);
  }, [mergedFilters, sortField, sortDirection]);

  const { deals, totalCount, totalPages, isLoading } = useCrmDealsPaginated(mergedFilters, {
    page,
    pageSize: GROUP_PAGE_SIZE,
    sortField,
    sortDirection,
  });

  const from = totalCount === 0 ? 0 : (page - 1) * GROUP_PAGE_SIZE + 1;
  const to = Math.min(page * GROUP_PAGE_SIZE, totalCount);

  return (
    <div className="bg-background">
      {isLoading ? (
        <div className="space-y-2 px-4 py-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : deals.length === 0 ? (
        <div className="px-6 py-4 text-xs text-muted-foreground italic">
          Nenhum negócio neste grupo.
        </div>
      ) : (
        <>
          {/* overflow-x-auto preserva as larguras nativas da tabela padrão */}
          <div className="overflow-x-auto">
            <DealsListTable
              deals={deals}
              scrollViewport={false}
              containerClassName=""
            />
          </div>

          {/* Paginação do grupo (setas) */}
          <div className="flex items-center justify-end gap-2 px-4 py-2 text-xs text-muted-foreground">
            <span>
              {from}-{to} de {totalCount}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              className="h-7 w-7"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              title="Página anterior"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              className="h-7 w-7"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              title="Próxima página"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
